import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NavBadgesResponse } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, type MerchantScope, useScopedMerchantClient } from "./scoped-merchant-client";

const navBadgesQueryKey = (scope: MerchantScope) => ["nav-badges", scope.merchantId, scope.workspaceId];

export const useNavBadges = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: navBadgesQueryKey(scope),
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["ui-interactions"]["nav-badges"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["ui-interactions"]["nav-badges"].$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useMarkNavBadgeViewed = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();
  const queryKey = navBadgesQueryKey(scope);

  return useMutation({
    mutationFn: async (navItemId: string) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["ui-interactions"]["nav-badges"][
            ":navItemId"
          ].view.$post({
            param: { ...scope.param, navItemId },
          })
        : await client.merchant[":merchantId"]["ui-interactions"]["nav-badges"][":navItemId"].view.$post({
            param: { ...scope.param, navItemId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onMutate: async (navItemId: string) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NavBadgesResponse>(queryKey);
      queryClient.setQueryData<NavBadgesResponse>(queryKey, (current) =>
        current ? { badges: current.badges.filter((badge) => badge.navItemId !== navItemId) } : current,
      );
      return { previous };
    },
    onError: (_error, _navItemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
